// Cost basis popup — how a Purchase Orders grid COST figure is made up.
//
// COST is actual job cost: the JDE job cost ledger entries (cost history,
// Job_Cost) on the line, summed. It is not open PO dollars — what's ordered
// but not yet received never posts to the job, so it's never in COST. The
// popup lists the entries by doc type (OV receipt, PV voucher, JE …) and,
// for a single PO line, sets its ordered / received / open next to it
// (v_Form_Open_PO_Items via jobOpenCommitmentsAtom).
import React, { useMemo } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import { View, Text, Pressable } from '../lib/react-native-compat';
import { pNum } from '../db/company';
import { jobOpenCommitmentsAtom } from '../store/company';
import { lineKey } from './billingInvoiceChase';

const money = (n) => {
    const v = pNum(n);
    const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (v < 0 ? '-$' : '$') + abs;
};
const fDate = (d) => (d ? moment.utc(d).format('M/D/YY') : '');

export const DOC_TYPES = {
    OV: 'PO receipt',
    PV: 'Voucher',
    JE: 'Journal entry',
    JP: 'P-card',
    PR: 'Voucher (no PO)',
    PD: 'Voucher',
    T1: 'Payroll',
    T2: 'Payroll',
};
export const docTypeLabel = (dct) => (dct ? `${DOC_TYPES[dct] || 'JDE entry'} (${dct})` : 'JDE entry');

// An open commitment row's open amount: the true open amount when JDE has
// one, else the plain open amount. The grid's OPEN AMOUNT column sums it.
export const ocOpen = (oc) => (oc.PO_True_Open_Amount != null ? pNum(oc.PO_True_Open_Amount) : pNum(oc.PO_Open_Amount));

// rows: the line's priced cost rows (billingPricing); oc: its open
// commitment row, if any. Ordered / open follow thirdPartyCostDrivers:
// ordered falls back to open + received, open prefers the true open amount.
export const costBasisOf = (rows = [], oc = null) => {
    const entries = [...rows].sort((a, b) => String(a.gl || a.date).localeCompare(String(b.gl || b.date)));
    const total = entries.reduce((s, t) => s + pNum(t.cost), 0);
    const byType = new Map();
    entries.forEach((t) => {
        const k = t.dct || '';
        const b = byType.get(k) || { dct: k, label: docTypeLabel(k), n: 0, cost: 0 };
        b.n += 1;
        b.cost += pNum(t.cost);
        byType.set(k, b);
    });
    let po = null;
    if (oc) {
        const received = pNum(oc.PO_Received_Amount);
        const open = ocOpen(oc);
        po = { ordered: pNum(oc.PO_Ordered_Amount) || open + received, received, open };
    }
    return { entries, total, byType: [...byType.values()], po };
};

const cell = { fontSize: 12, color: '#1e293b', paddingTop: 5, paddingBottom: 5, paddingLeft: 6, paddingRight: 6 };
const head = { fontSize: 10, fontWeight: '700', color: '#64748b', letterSpacing: 0.4, paddingTop: 4, paddingBottom: 4, paddingLeft: 6, paddingRight: 6 };

// target: { title, sub, rows, po, pol } — pol set when it is one PO line.
const CostBasisModal = ({ target, onClose }) => {
    const [openCommitments] = useAtom(jobOpenCommitmentsAtom);
    const oc = useMemo(() => {
        if (!target?.po || !target?.pol || !Array.isArray(openCommitments)) return null;
        const k = lineKey(target.po, target.pol);
        return openCommitments.find((o) => o && o.PO_Number != null && lineKey(o.PO_Number, o.PO_Line_Number) === k) || null;
    }, [target, openCommitments]);
    const basis = useMemo(() => costBasisOf(target?.rows || [], oc), [target, oc]);
    if (!target) return null;
    const { entries, total, byType, po } = basis;
    return (
        <Pressable
            onPress={onClose}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50,
                backgroundColor: 'rgba(15, 23, 42, 0.5)', alignItems: 'center', justifyContent: 'center',
                cursor: 'default', padding: 24,
            }}
        >
            <Pressable
                onPress={(e) => e.stopPropagation && e.stopPropagation()}
                style={{
                    backgroundColor: '#fff', borderRadius: 8, width: 640, maxWidth: '96vw', maxHeight: '80vh',
                    overflowY: 'auto', boxShadow: '0 20px 60px rgba(15, 23, 42, 0.3)', cursor: 'default',
                }}
            >
                <View style={{ paddingTop: 16, paddingBottom: 12, paddingLeft: 22, paddingRight: 22, borderBottomWidth: 1, borderColor: '#e2e8f0', flexDirection: 'row', alignItems: 'center' }}>
                    <View style={{ flexGrow: 1, flexShrink: 1 }}>
                        <Text style={{ fontSize: 16, fontWeight: '700', color: '#0f172a' }}>How COST is calculated</Text>
                        <Text style={{ fontSize: 12, color: '#64748b', marginTop: 2 }} numberOfLines={1}>{target.title}</Text>
                    </View>
                    <Pressable onPress={onClose} style={{ marginLeft: 14 }}>
                        <Text style={{ fontSize: 18, color: '#64748b' }}>×</Text>
                    </Pressable>
                </View>
                <View style={{ paddingTop: 12, paddingBottom: 16, paddingLeft: 22, paddingRight: 22 }}>
                    <Text style={{ fontSize: 12, color: '#334155', lineHeight: 18 }}>
                        COST is actual cost posted to the job in JDE: the sum of the job cost entries below.
                        It does not include open PO dollars (ordered but not yet received).
                        Those only reach COST once they're received or vouchered.
                    </Text>

                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 12 }}>
                        {byType.map((b) => (
                            <View key={b.dct} style={{ borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 6, paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10, marginRight: 8, marginBottom: 8 }}>
                                <Text style={{ fontSize: 10, color: '#64748b' }}>{b.label} · {b.n} {b.n === 1 ? 'entry' : 'entries'}</Text>
                                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>{money(b.cost)}</Text>
                            </View>
                        ))}
                        <View style={{ backgroundColor: '#0f172a', borderRadius: 6, paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10, marginBottom: 8 }}>
                            <Text style={{ fontSize: 10, color: '#cbd5e1' }}>COST</Text>
                            <Text style={{ fontSize: 13, fontWeight: '700', color: '#fff' }}>{money(total)}</Text>
                        </View>
                    </View>

                    <View style={{ borderTopWidth: 1, borderColor: '#e2e8f0', marginTop: 4 }}>
                        <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                            <Text style={[head, { width: 70 }]}>GL DATE</Text>
                            <Text style={[head, { width: 150 }]}>JDE DOC TYPE</Text>
                            <Text style={[head, { flexGrow: 1, flexShrink: 1 }]}>DESCRIPTION</Text>
                            <Text style={[head, { width: 100, textAlign: 'right' }]}>AMOUNT</Text>
                        </View>
                        {entries.map((t, i) => (
                            <View key={t.key || i} style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#f1f5f9' }}>
                                <Text style={[cell, { width: 70, color: '#64748b' }]}>{fDate(t.gl || t.date)}</Text>
                                <Text style={[cell, { width: 150 }]}>{docTypeLabel(t.dct)}</Text>
                                <Text style={[cell, { flexGrow: 1, flexShrink: 1 }]} numberOfLines={1}>{t.ds || t.ctDesc || '—'}</Text>
                                <Text style={[cell, { width: 100, textAlign: 'right' }]}>{money(t.cost)}</Text>
                            </View>
                        ))}
                        <View style={{ flexDirection: 'row' }}>
                            <Text style={[cell, { flexGrow: 1, fontWeight: '700' }]}>COST ({entries.length} {entries.length === 1 ? 'entry' : 'entries'})</Text>
                            <Text style={[cell, { width: 100, textAlign: 'right', fontWeight: '700' }]}>{money(total)}</Text>
                        </View>
                    </View>

                    {po ? (
                        <View style={{ marginTop: 14, backgroundColor: '#f8fafc', borderRadius: 6, paddingTop: 10, paddingBottom: 10, paddingLeft: 12, paddingRight: 12 }}>
                            <Text style={{ fontSize: 11, fontWeight: '700', color: '#475569', marginBottom: 6 }}>THIS PO LINE IN JDE PURCHASING, FOR CONTEXT</Text>
                            {[
                                ['Ordered', po.ordered, ''],
                                ['Received', po.received, 'posts to the job as cost'],
                                ['Open (not received yet)', po.open, 'not in COST'],
                            ].map(([k, v, note]) => (
                                <View key={k} style={{ flexDirection: 'row', alignItems: 'baseline', paddingTop: 2, paddingBottom: 2 }}>
                                    <Text style={{ fontSize: 12, color: '#334155', width: 170 }}>{k}</Text>
                                    <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: '600', width: 100, textAlign: 'right' }}>{money(v)}</Text>
                                    <Text style={{ fontSize: 11, color: '#94a3b8', marginLeft: 10 }}>{note}</Text>
                                </View>
                            ))}
                            <Text style={{ fontSize: 11, color: '#64748b', marginTop: 6, lineHeight: 16 }}>
                                Received and COST can differ. Sales tax posts as its own entry on the receipt,
                                and a vendor invoice priced differently from the receipt can post the difference on the PV.
                            </Text>
                        </View>
                    ) : null}
                </View>
            </Pressable>
        </Pressable>
    );
};

export default CostBasisModal;
